import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";
import { openAttachment } from "./attachmentViewer";

// Plugins that ship with the app. Anything else is looked up on the bridge proxy.
const BUNDLED_PLUGINS = { Browser, Filesystem, Share };

const PREVIEWABLE_MIMES = [
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
  "text/plain",
];

export function getCapacitor() {
  if (typeof window === "undefined") return null;
  return window.Capacitor || Capacitor || null;
}

export function getNativePlatform() {
  const capacitor = getCapacitor();
  return String(capacitor?.getPlatform?.() || "").toLowerCase();
}

export function isNativeApp() {
  const capacitor = getCapacitor();
  if (!capacitor) return false;
  if (typeof capacitor.isNativePlatform === "function") {
    return !!capacitor.isNativePlatform();
  }
  return ["ios", "android"].includes(getNativePlatform());
}

export function isNativeAndroidApp() {
  return isNativeApp() && getNativePlatform() === "android";
}

export function isStandaloneMode() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches === true ||
    window.navigator?.standalone === true
  );
}

export function getCapacitorPlugin(name) {
  return BUNDLED_PLUGINS[name] || getCapacitor()?.Plugins?.[name] || null;
}

/** Opens an external URL in the system browser (SFSafariViewController / Chrome Custom Tab) on native, or a new tab on the web. */
export async function openExternalUrl(url) {
  if (!url) return;
  if (isNativeApp()) {
    await Browser.open({ url, presentationStyle: "popover" });
    return;
  }
  window.open(url, "_blank", "noopener,noreferrer");
}

export async function callCapacitorPlugin(name, method, payload) {
  const plugin = getCapacitorPlugin(name);
  const fn = plugin?.[method];
  if (typeof fn !== "function") return null;
  return fn.call(plugin, payload);
}

const blobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () =>
      reject(reader.error || new Error("Failed to read file"));
    reader.onload = () => {
      const result = String(reader.result || "");
      const [, base64 = ""] = result.split(",");
      resolve(base64);
    };
    reader.readAsDataURL(blob);
  });

export async function handleNativeBlob(blob, filename, mimeType) {
  const capacitor = getCapacitor();
  const Filesystem = getCapacitorPlugin("Filesystem");
  const Browser = getCapacitorPlugin("Browser");
  const Share = getCapacitorPlugin("Share");

  if (!capacitor || !Filesystem) {
    return false;
  }

  const base64Data = await blobToBase64(blob);
  const normalizedName =
    filename || `download.${mimeType?.split("/")?.pop?.() || "dat"}`;
  const targetPath = `downloads/${Date.now()}-${normalizedName.replace(
    /[^\w.\-]/g,
    "_"
  )}`;
  const directory = Directory?.Cache || Directory?.Documents || undefined;

  const writeResult = await Filesystem.writeFile({
    path: targetPath,
    data: base64Data,
    directory,
    recursive: true,
  });

  const nativeUri = writeResult?.uri;
  const convertedUrl =
    nativeUri && typeof capacitor.convertFileSrc === "function"
      ? capacitor.convertFileSrc(nativeUri)
      : nativeUri;
  const previewable = PREVIEWABLE_MIMES.includes(
    String(mimeType || "").toLowerCase()
  );

  if (previewable && Browser && convertedUrl) {
    await Browser.open({ url: convertedUrl });
    return true;
  }

  if (Share && (nativeUri || convertedUrl)) {
    const payload = {
      title: normalizedName,
      dialogTitle: normalizedName,
      url: nativeUri || convertedUrl,
    };
    await Share.share(payload);
    return true;
  }

  if (convertedUrl) {
    window.open(convertedUrl, "_blank", "noopener,noreferrer");
    return true;
  }

  return false;
}

export async function downloadBlob(blob, filename, options = {}) {
  const mimeType = options.mimeType || blob?.type || "application/octet-stream";
  const previewable = options.previewable === true;

  if (isNativeApp()) {
    const handled = await handleNativeBlob(blob, filename, mimeType);
    if (handled) return;
  }

  if (previewable && openAttachment({ name: filename, load: async () => blob })) {
    return;
  }

  const url = window.URL.createObjectURL(blob);

  if (previewable) {
    const newWindow = window.open(url, "_blank");
    if (newWindow && !newWindow.closed) {
      newWindow.onbeforeunload = () => window.URL.revokeObjectURL(url);
      return;
    }
  }

  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
